import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type {
    BariPreNurseSpecialistMedScreeningReportBaseReadLabel,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle,
} from '../bariPreNurseSpecialistMedScreeningReportReadSections';
import type { BariPreNurseSpecialistMedScreeningReportDataV1 } from './bariPreNurseSpecialistMedScreeningReportDataV1';
import {
    aanvullendeOnderzoekenReadSection,
    anamneseReadSection,
    conclusieReadSection,
    familiaireBelastingReadSection,
    lichamelijkOnderzoekReadSection,
    medicatieReadSection,
    specieleAnamneseReadSection,
} from '../bariPreNurseSpecialistMedScreeningReportReadSections';

export type BariPreNurseSpecialistMedScreeningReportReadDataV1 = BariPreNurseSpecialistMedScreeningReportDataV1 & {
    aanvullende_onderzoeken: string;
    conclusie: string;
    overgewicht_broer_zus: boolean;
    overgewicht_moeder: boolean;
    overgewicht_vader: boolean;
};

export type BariPreNurseSpecialistMedScreeningReportReadKeyV1 =
    keyof BariPreNurseSpecialistMedScreeningReportReadDataV1;

export type BariPreNurseSpecialistMedScreeningReportReadLabelV1 = BariPreNurseSpecialistMedScreeningReportBaseReadLabel;

export type BariPreNurseSpecialistMedScreeningReportReadSectionTitleV1 =
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle;

export const bariPreNurseSpecialistMedScreeningReportReadSchemaV1: TypedReadSchema<
    BariPreNurseSpecialistMedScreeningReportReadKeyV1,
    BariPreNurseSpecialistMedScreeningReportReadLabelV1,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitleV1
>[] = [
    anamneseReadSection,
    specieleAnamneseReadSection,
    medicatieReadSection,
    familiaireBelastingReadSection,
    {
        items: [
            { key: 'myocardinfarct', label: 'Myocardinfarct', type: 'boolean' },
            { key: 'congestief_hartfalen', label: 'Congestief hartfalen', type: 'boolean' },
            {
                key: 'perifeer_vaatlijden_aneurysma_aorta',
                label: 'Perifeer vaatlijden/aneurysma aorta',
                type: 'boolean',
            },
            { key: 'cerebrovasculaire_aandoening', label: 'Cerebrovasculaire aandoening', type: 'boolean' },
            { key: 'dementie', label: 'Dementie', type: 'boolean' },
            { key: 'chronische_longziekte', label: 'Chronische longziekte', type: 'boolean' },
            { key: 'verhoogd_risico_DVT_of_PE', label: 'Verhoogd risico DVT of PE', type: 'boolean' },
            { key: 'gastrointestinaal_ulcuslijden', label: 'Gastro-intestinaal ulcuslijden', type: 'boolean' },
            { key: 'leverziekte', label: 'Leverziekte' },
            { key: 'para_hemiplegie', label: 'Para-/hemiplegie', type: 'boolean' },
            { key: 'nierziekte', label: 'Nierziekte', type: 'boolean' },
            { key: 'maligniteit', label: 'Maligniteit (excl. PCC of BCC van de huid)' },
            { key: 'HIV_AIDS', label: 'HIV/AIDS', type: 'boolean' },
            { key: 'bindweefselziekte', label: 'Bindweefselziekte (o.a. reumatoïde ziekten)', type: 'boolean' },

            { key: 'hypertensie_diagnose', label: 'Hypertensie (diagnose)' },
            { key: 'diabetes_mellitus_diagnose', label: 'Diabetes mellitus (diagnose)' },
            { key: 'diabetes_mellitus_details', label: 'Details diabetes mellitus' },
            { append: 'mmol/mol', key: 'hba1c', label: 'HbA1c', type: 'number' },
            { key: 'dyslipidemie', label: 'Dyslipidemie (diagnose)' },
            { append: 'mmol/l', key: 'LDL_cholesterol', label: 'LDL-cholesterol', type: 'number' },
            { append: 'mmol/l', key: 'HDL_cholesterol', label: 'HDL-cholesterol', type: 'number' },
            { append: 'mmol/l', key: 'triglyceriden', label: 'Triglyceriden', type: 'number' },
            { key: 'GERD', label: 'GERD (diagnose)' },
            { key: 'OSAS', label: 'OSAS (diagnose)' },
            { key: 'gewrichtsklachten', label: 'Gewrichtsklachten (diagnose)' },
            { key: 'klachten_van_reflux', label: 'Klachten van reflux', type: 'boolean' },
            { key: 'trombose', label: 'Trombose', type: 'boolean' },
        ],
        sectionTitle: 'Comorbiditeiten',
    },
    lichamelijkOnderzoekReadSection,
    aanvullendeOnderzoekenReadSection,
    conclusieReadSection,
];
